import { useState } from "react"
import { createPortal } from "react-dom"
import { useErrorOverlayStore, type ErrorEntry } from "../../stores/error-overlay-store"
import { Warning, Trash, CaretDown, Copy, X } from "@phosphor-icons/react"
import { Button } from "../ui/button"
import { Badge } from "../ui/badge"
import { cn } from "../../lib/utils"

export function ErrorOverlay() {
  const { errors, isOpen, toggleOpen, clearAll, removeError } = useErrorOverlayStore()

  if (errors.length === 0) return null

  return createPortal(
    <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 99998 }}>
      {!isOpen && (
        <button
          onClick={toggleOpen}
          className={cn(
            "pointer-events-auto",
            "fixed bottom-4 left-4",
            "flex items-center gap-2 px-3 py-2 rounded-full",
            "bg-danger text-danger-foreground",
            "shadow-lg hover:shadow-xl",
            "text-xs font-semibold",
            "transition-all hover:scale-105",
          )}
        >
          <Warning className="h-4 w-4" weight="fill" />
          <span>
            {errors.length} issue{errors.length !== 1 ? "s" : ""}
          </span>
        </button>
      )}

      {isOpen && (
        <div
          className={cn(
            "pointer-events-auto",
            "fixed bottom-4 left-4",
            "w-[400px] max-h-[400px]",
            "bg-popover text-popover-foreground",
            "border border-border rounded-lg shadow-xl",
            "flex flex-col",
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
            <div className="flex items-center gap-2">
              <Warning className="h-4 w-4 text-danger" weight="fill" />
              <span className="font-semibold text-sm">Issues ({errors.length})</span>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon-xs" onClick={clearAll} title="Clear all">
                <Trash className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="icon-xs" onClick={toggleOpen} title="Minimize">
                <CaretDown className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Hint */}
          <div className="px-4 py-2 border-b border-border bg-muted/50">
            <p className="text-[11px] text-muted-foreground">
              Copy the below and send it to the team monitoring your system.
            </p>
          </div>

          {/* Error list */}
          <div className="flex-1 overflow-auto min-h-0">
            {errors.map((entry) => (
              <ErrorEntryCard key={entry.id} entry={entry} onRemove={removeError} />
            ))}
          </div>
        </div>
      )}
    </div>,
    document.body,
  )
}

const SOURCE_CONFIG: Record<string, { label: string; variant: "danger" | "warning" | "purple" }> = {
  react: { label: "React", variant: "danger" },
  runtime: { label: "Runtime", variant: "warning" },
  promise: { label: "Promise", variant: "purple" },
}

function ErrorEntryCard({
  entry,
  onRemove,
}: {
  entry: ErrorEntry
  onRemove: (id: string) => void
}) {
  const [expanded, setExpanded] = useState(true)
  const timeStr = new Date(entry.timestamp).toLocaleTimeString()
  const config = SOURCE_CONFIG[entry.source] ?? { label: entry.source, variant: "secondary" as const }

  return (
    <div className="border-b border-border last:border-b-0">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left px-4 py-2.5 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge variant={config.variant} className="text-[10px] px-1.5 py-0">
                {config.label}
              </Badge>
              <span className="text-[10px] text-muted-foreground">{timeStr}</span>
              {entry.count > 1 && (
                <span className="text-[10px] text-muted-foreground">x{entry.count}</span>
              )}
            </div>
            <p className="text-xs font-mono text-danger truncate">{entry.message}</p>
          </div>
          <CaretDown
            className={cn(
              "h-3 w-3 text-muted-foreground transition-transform shrink-0 mt-1",
              expanded && "rotate-180",
            )}
          />
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-3 space-y-2">
          <div className="flex gap-1.5">
            <Button
              variant="ghost"
              size="xs"
              onClick={() => {
                const text = [
                  entry.message,
                  `URL: ${entry.url}`,
                  entry.fileLocation ? `File: ${entry.fileLocation}` : null,
                  entry.userId ? `User: ${entry.userId}` : null,
                  `Browser: ${entry.userAgent}`,
                  entry.stack,
                  entry.componentStack,
                ]
                  .filter(Boolean)
                  .join("\n\n")
                navigator.clipboard.writeText(text)
              }}
            >
              <Copy className="h-3 w-3 mr-1" /> Copy
            </Button>
            <Button variant="ghost" size="xs" onClick={() => onRemove(entry.id)}>
              <X className="h-3 w-3 mr-1" /> Remove
            </Button>
          </div>

          <div className="text-[10px] text-muted-foreground space-y-0.5">
            <div className="font-mono break-all">{entry.url}</div>
            {entry.fileLocation && <div className="font-mono">File: {entry.fileLocation}</div>}
            {entry.userId && <div>User: {entry.userId}</div>}
          </div>

          {entry.stack && (
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground mb-1">Stack Trace</p>
              <pre className="text-[10px] font-mono bg-muted p-2 rounded overflow-x-auto max-h-[200px] overflow-y-auto whitespace-pre-wrap break-all">
                {entry.stack}
              </pre>
            </div>
          )}

          {entry.componentStack && (
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground mb-1">
                Component Stack
              </p>
              <pre className="text-[10px] font-mono bg-muted p-2 rounded overflow-x-auto max-h-[150px] overflow-y-auto whitespace-pre-wrap break-all">
                {entry.componentStack}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
